import { Injectable, inject } from "@angular/core";
import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import {
	MEMOTAIN_PRODUCT_KEYS,
	FUNCTIONAL_PRODUCT_KEYS,
	ProductKey,
	PROFESSIONAL_PRODUCT_KEYS,
	RETENTIONSSCHIENE_PRODUCT_DATA_KEYS,
	PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS,
	PEEK_RETAINER_PRODUCT_KEYS
} from "../models/product.model";

// categories / logos
export const MEMOTAIN_LOGO_ASSET_URL = "assets/img/products/logo-mt.jpg";
export const MEMOTAIN_CLEAR_LOGO_ASSET_URL = "assets/img/products/logo-mt-clear.jpg";
export const MEMOTAIN_PEEK_3D_LOGO_ASSET_URL = "assets/img/products/logo-mt-peek-3d.jpg";
export const PRO_LOGO_ASSET_URL = "assets/img/products/logo-professional.png";
export const FUNCTIONAL_LOGO_ASSET_URL = "assets/img/products/logo-functional.png";
export const LEGACY_CA_LOGO_ASSET_URL = "assets/img/products/logo-ca_legacy.jpg";
export const MICHIGAN_SPLINT_LOGO_ASSET_DE_URL = "assets/img/products/Funktionsschiene_dt.jpg";
export const MICHIGAN_SPLINT_LOGO_ASSET_ENG_URL = "assets/img/products/Funktionsschiene_dt.jpg";

export const MEMOTAIN_CATEGORY_ASSET_URL = "assets/img/products/category-mt.jpg";
export const MEMOTAIN_CLEAR_CATEGORY_ASSET_URL = "assets/img/products/category-mt-clear.jpg";
export const MEMOTAIN_PEEK_3D_CATEGORY_ASSET_URL = "assets/img/products/category-mt-peek-3d.jpg";

export const CATEGORY_DATA_VERSION_ASSET_URL = "assets/img/products/produktbild_datenvariante.png";
export const CATEGORY_SPLINT_VERSION_ASSET_URL = "assets/img/products/produktbild_schienenvariante.png";

// product images
export const PRO_SMILE_IMAGE_ASSET_URL = "assets/img/products/professional_smile.png";
export const PRO_LITE_IMAGE_ASSET_URL = "assets/img/products/professional_lite.png";
export const PRO_FULL_IMAGE_ASSET_URL = "assets/img/products/professional_full.png";
export const PRO_ASSESSMENT_IMAGE_ASSET_URL = "assets/img/products/professional_contact.png";
export const FUNCTIONAL_NEO_IMAGE_ASSET_URL = "assets/img/products/functional_neo.jpg";
export const FUNCTIONAL_PLUS_IMAGE_ASSET_URL = "assets/img/products/functional_plus.jpg";
export const FUNCTIONAL_MAX_IMAGE_ASSET_URL = "assets/img/products/functional_max.jpg";
export const FUNCTIONAL_ASSESSMENT_IMAGE_ASSET_URL = "assets/img/products/functional_assessment.jpg";
export const MEMOTAIN_IMAGE_ASSET_URL = "assets/img/products/produktbild_mt.jpg";
export const RETENTION_FOIL_IMAGE_ASSET_URL = "assets/img/products/produktbild_mt_clear.jpg";
export const PEEK_RETAINER_IMAGE_ASSET_URL = "assets/img/products/produktbild_mt_peek_3d.jpg";

// miscellaneous
export const TOOTHAREA_PRO_SMILE_ASSET_URL = "assets/img/products/zahnkranz_smile.png";
export const TOOTHAREA_PRO_LITE_ASSET_URL = "assets/img/products/zahnkranz_lite.png";
export const TOOTHAREA_PRO_FULL_ASSET_URL = "assets/img/products/zahnkranz_full.png";

export const TOOTHAREA_FUNCTIONAL_NEO_ASSET_URL = "assets/img/products/zahnkranz_functional_neo.jpg";
export const TOOTHAREA_FUNCTIONAL_PLUS_ASSET_URL = "assets/img/products/zahnkranz_functional_plus.jpg";
export const TOOTHAREA_FUNCTIONAL_MAX_ASSET_URL = "assets/img/products/zahnkranz_functional_max.jpg";

export const INDICATION_PRO_SMILE_DE_ASSET_URL = "assets/img/products/indications/de/indikation_de_prof_smile.png";
export const INDICATION_PRO_LITE_DE_ASSET_URL = "assets/img/products/indications/de/indikation_de_prof_lite.png";
export const INDICATION_PRO_FULL_DE_ASSET_URL = "assets/img/products/indications/de/indikation_de_prof_full.png";
export const INDICATION_PRO_SMILE_EN_ASSET_URL = "assets/img/products/indications/en/indikation_en_prof_smile.png";
export const INDICATION_PRO_LITE_EN_ASSET_URL = "assets/img/products/indications/en/indikation_en_prof_lite.png";
export const INDICATION_PRO_FULL_EN_ASSET_URL = "assets/img/products/indications/en/indikation_en_prof_full.png";

@Injectable({
	providedIn: "root"
})
export class ProductAssetsService {
	config = inject<AppConfig>(APP_CONFIG_TOKEN);

	/**
	 * Returns the image url of the product category (not the image of the product itself)
	 */
	getProductCategoryImageUrl(productKey: ProductKey): string {
		if (MEMOTAIN_PRODUCT_KEYS.includes(productKey)) {
			return MEMOTAIN_CATEGORY_ASSET_URL;
		}
		if (PEEK_RETAINER_PRODUCT_KEYS.includes(productKey)) {
			return MEMOTAIN_PEEK_3D_CATEGORY_ASSET_URL;
		}
		if (RETENTIONSSCHIENE_PRODUCT_DATA_KEYS.includes(productKey)) {
			return MEMOTAIN_CLEAR_CATEGORY_ASSET_URL;
		}
		if (
			[...PROFESSIONAL_PRODUCT_KEYS, ...PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS].includes(productKey)
		) {
			return PRO_LOGO_ASSET_URL;
		}
		if ([...FUNCTIONAL_PRODUCT_KEYS, ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT].includes(productKey)) {
			return FUNCTIONAL_LOGO_ASSET_URL;
		}
		if (productKey === ProductKey.MICHIGAN_SPLINT) {
			return this.config.locale === "de"
				? MICHIGAN_SPLINT_LOGO_ASSET_DE_URL
				: MICHIGAN_SPLINT_LOGO_ASSET_ENG_URL;
		}
		//Legacy products
		return LEGACY_CA_LOGO_ASSET_URL;
	}

	/**
	 * Returns the image url of the product image (not the category/logo)
	 */
	getProductImageUrl(productKey: ProductKey): string {
		if(MEMOTAIN_PRODUCT_KEYS.includes(productKey) ){
			return MEMOTAIN_IMAGE_ASSET_URL;
		}
		if(RETENTIONSSCHIENE_PRODUCT_DATA_KEYS.includes(productKey)) {
			return RETENTION_FOIL_IMAGE_ASSET_URL;
		}
		if(PEEK_RETAINER_PRODUCT_KEYS.includes(productKey)) {
			return PEEK_RETAINER_IMAGE_ASSET_URL;
		}
		switch(productKey){
			case ProductKey.PRO_SMILE_DATEN:
			case ProductKey.PRO_SMILE_SCHIENE:
				return PRO_SMILE_IMAGE_ASSET_URL;
			case ProductKey.PRO_LITE_DATEN:
			case ProductKey.PRO_LITE_SCHIENE:
				return PRO_LITE_IMAGE_ASSET_URL
			case ProductKey.PRO_FULL_DATEN:
			case ProductKey.PRO_FULL_SCHIENE:
				return PRO_FULL_IMAGE_ASSET_URL;
			case ProductKey.PRO_CASE_ASSESSMENT_DATEN:
			case ProductKey.PRO_CASE_ASSESSMENT_SCHIENE:
				return PRO_ASSESSMENT_IMAGE_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT:
				return FUNCTIONAL_ASSESSMENT_IMAGE_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_NEO:
				return FUNCTIONAL_NEO_IMAGE_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_PLUS:
				return FUNCTIONAL_PLUS_IMAGE_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_MAX:
				return FUNCTIONAL_MAX_IMAGE_ASSET_URL;
			case ProductKey.MICHIGAN_SPLINT:
				return this.config.locale === 'de' ? MICHIGAN_SPLINT_LOGO_ASSET_DE_URL : MICHIGAN_SPLINT_LOGO_ASSET_ENG_URL;
			default:
				return LEGACY_CA_LOGO_ASSET_URL;
		}
	}

	/**
	 * Function that is exclusivly used in the product selection.
	 * Returns the image urls for the different products.
	 */
	getProductSelectionImageUrl( productKey: ProductKey): string {
		if(MEMOTAIN_PRODUCT_KEYS.includes(productKey) ){
			return MEMOTAIN_LOGO_ASSET_URL;
		}
		if(RETENTIONSSCHIENE_PRODUCT_DATA_KEYS.includes(productKey)) {
			return MEMOTAIN_CLEAR_LOGO_ASSET_URL;
		}
		if(PEEK_RETAINER_PRODUCT_KEYS.includes(productKey)) {
			return MEMOTAIN_PEEK_3D_LOGO_ASSET_URL;
		}
		switch (productKey){
			case ProductKey.FUNCTIONAL_SCHIENE_NEO:
				return FUNCTIONAL_NEO_IMAGE_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_PLUS:
				return FUNCTIONAL_PLUS_IMAGE_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_MAX:
				return FUNCTIONAL_MAX_IMAGE_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT:
				return FUNCTIONAL_LOGO_ASSET_URL;
			case ProductKey.PRO_SMILE_SCHIENE:
			case ProductKey.PRO_SMILE_DATEN:
				return PRO_SMILE_IMAGE_ASSET_URL;
			case ProductKey.PRO_LITE_SCHIENE:
			case ProductKey.PRO_LITE_DATEN:
				return PRO_LITE_IMAGE_ASSET_URL;
			case ProductKey.PRO_FULL_SCHIENE:
			case ProductKey.PRO_FULL_DATEN:
				return PRO_FULL_IMAGE_ASSET_URL;
			case ProductKey.PRO_CASE_ASSESSMENT_DATEN:
			case ProductKey.PRO_CASE_ASSESSMENT_SCHIENE:
				return PRO_ASSESSMENT_IMAGE_ASSET_URL;
			case ProductKey.MICHIGAN_SPLINT:
				return this.config.locale === 'de' ? MICHIGAN_SPLINT_LOGO_ASSET_DE_URL : MICHIGAN_SPLINT_LOGO_ASSET_ENG_URL;
		}
	}

	getToothAreaImageUrl(productKey: ProductKey): string {
		switch (productKey) {
			case ProductKey.PRO_SMILE_DATEN:
			case ProductKey.PRO_SMILE_SCHIENE:
				return TOOTHAREA_PRO_SMILE_ASSET_URL;
			case ProductKey.PRO_LITE_DATEN:
			case ProductKey.PRO_LITE_SCHIENE:
				return TOOTHAREA_PRO_LITE_ASSET_URL;
			case ProductKey.PRO_FULL_DATEN:
			case ProductKey.PRO_FULL_SCHIENE:
				return TOOTHAREA_PRO_FULL_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_NEO:
				return TOOTHAREA_FUNCTIONAL_NEO_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_PLUS:
				return TOOTHAREA_FUNCTIONAL_PLUS_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_MAX:
				return TOOTHAREA_FUNCTIONAL_MAX_ASSET_URL;
			case ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT:
				return FUNCTIONAL_ASSESSMENT_IMAGE_ASSET_URL;
		}
	}

	getProductIndicationImageUrl(productKey: ProductKey): string {
		const locale = this.config.locale;
		switch(productKey) {
			case ProductKey.PRO_SMILE_DATEN:
			case ProductKey.PRO_SMILE_SCHIENE:
				return locale === 'de' ? INDICATION_PRO_SMILE_DE_ASSET_URL : INDICATION_PRO_SMILE_EN_ASSET_URL;
			case ProductKey.PRO_LITE_DATEN:
			case ProductKey.PRO_LITE_SCHIENE:
				return locale === 'de' ? INDICATION_PRO_LITE_DE_ASSET_URL : INDICATION_PRO_LITE_EN_ASSET_URL;
			case ProductKey.PRO_FULL_DATEN:
			case ProductKey.PRO_FULL_SCHIENE:
				return locale === 'de' ? INDICATION_PRO_FULL_DE_ASSET_URL : INDICATION_PRO_FULL_EN_ASSET_URL;
			default:
				return '';
		}
	}
}
